import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { Typography } from '@mui/material';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import { PDCDashboard } from './PDCDashboard';
import { PDCCharts } from './PDCCharts';
import { PDCFindingsTable } from './PDCFindingsTable';
import {
  collectPDCRuns,
  createVersionId,
  formatPDCVersionTimestamp,
  formatPDCVersionTitle,
  matchesPDCRunId,
  sortPDCRunsNewestFirst,
  type PDCRun
} from './pdc.utils';
import '../Bias/BiasDetectionChartsSection.css';

export interface PromptInjectionPDCRef {
  /** Prompt injection is LLM-only; `lane` is accepted for nav API compatibility. */
  handleHistoryOpen: (lane?: 'ml' | 'llm') => void;
}

interface PromptInjectionPDCProps {
  customData?: unknown;
  projectUploads?: unknown;
  showInlineVersionControls?: boolean;
  /** When false, Compliance IQ mode: charts only (no KPI cards or findings table). */
  showDashboardKpis?: boolean;
  onVersionsReady?: (info: { total: number; runsWithMl: number; runsWithLlm: number }) => void;
}

function runsToVersionItems(runs: PDCRun[], selectedRunId: string): VersionItem[] {
  return runs.map((run, index) => {
    const id = createVersionId(run, index);
    const timestamp = formatPDCVersionTimestamp(run.timestamp) || 'Run';
    return {
      id,
      timestamp,
      date: timestamp,
      modifier: String(run.userId ?? 'System'),
      isCurrent: selectedRunId === id,
      title: formatPDCVersionTitle(run, index)
    };
  });
}

export const PromptInjectionPDC = forwardRef<PromptInjectionPDCRef, PromptInjectionPDCProps>(
  (
    { customData, projectUploads, showInlineVersionControls = true, showDashboardKpis = true, onVersionsReady },
    ref
  ) => {
    const projectResults = useSelector(selectProjectResults);
    const onVersionsReadyRef = useRef(onVersionsReady);
    onVersionsReadyRef.current = onVersionsReady;

    const [showHistory, setShowHistory] = useState(false);
    const [selectedRunId, setSelectedRunId] = useState('');

    const allRuns = useMemo(
      () => sortPDCRunsNewestFirst(collectPDCRuns(projectResults, customData, projectUploads)),
      [projectResults, customData, projectUploads]
    );

    useEffect(() => {
      onVersionsReadyRef.current?.({
        total: allRuns.length,
        runsWithMl: 0,
        runsWithLlm: allRuns.length
      });
    }, [allRuns.length]);

    useEffect(() => {
      if (allRuns.length === 0) {
        setSelectedRunId('');
        return;
      }
      const exists = allRuns.some(run => matchesPDCRunId(run, selectedRunId));
      if (!selectedRunId || !exists) {
        setSelectedRunId(createVersionId(allRuns[0], 0));
      }
    }, [allRuns, selectedRunId]);

    const selectedRun = useMemo(
      () => allRuns.find(run => matchesPDCRunId(run, selectedRunId)) ?? allRuns[0] ?? null,
      [allRuns, selectedRunId]
    );

    const versionItems = useMemo(() => runsToVersionItems(allRuns, selectedRunId), [allRuns, selectedRunId]);

    const handleVersionHistorySelect = (item: VersionItem) => {
      setSelectedRunId(item.id);
      setShowHistory(false);
    };

    useImperativeHandle(ref, () => ({
      handleHistoryOpen: () => setShowHistory(true)
    }));

    if (allRuns.length === 0) {
      return (
        <Container fluid className="px-0">
          {showInlineVersionControls ? (
            <Typography variant="h5" className="mb-3">
              Prompt Injection Detection
            </Typography>
          ) : null}
          <p className="text-muted">No Prompt Injection evaluation runs found for this project.</p>
        </Container>
      );
    }

    return (
      <Container fluid className="px-0">
        {showInlineVersionControls ? (
          <Row className="align-items-center mb-3 g-2">
            <Col>
              <Typography variant="h5" className="mb-0">
                Prompt Injection Detection
              </Typography>
            </Col>
            {allRuns.length > 1 ? (
              <Col xs="auto">
                <Form.Select
                  size="sm"
                  value={selectedRunId}
                  onChange={e => setSelectedRunId(e.target.value)}
                  aria-label="Select Prompt Injection evaluation version"
                  style={{ minWidth: 220 }}
                >
                  {allRuns.map((run, index) => {
                    const id = createVersionId(run, index);
                    return (
                      <option key={id} value={id}>
                        {formatPDCVersionTitle(run, index)}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
            ) : null}
          </Row>
        ) : null}

        {selectedRun ? (
          <>
            {showDashboardKpis ? <PDCDashboard run={selectedRun} /> : null}
            <PDCCharts run={selectedRun} />
            {showDashboardKpis ? <PDCFindingsTable run={selectedRun} /> : null}
          </>
        ) : (
          <p className="text-muted small py-3">No Prompt Injection evaluation data available for this project.</p>
        )}

        <VersionHistory
          open={showHistory}
          onClose={() => setShowHistory(false)}
          versions={versionItems}
          onVersionSelect={handleVersionHistorySelect}
          title="Prompt Injection — LLM version history"
        />
      </Container>
    );
  }
);

PromptInjectionPDC.displayName = 'PromptInjectionPDC';

export default PromptInjectionPDC;
